import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AuthShell from "@/components/AuthShell";
import TwoFactorSetup from "@/components/TwoFactorSetup";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getInvite, acceptInvite, totpConfirm, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, ArrowRight, XCircle } from "lucide-react";

const InviteAccept = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [invite, setInvite] = useState(undefined);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [setupData, setSetupData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    getInvite(token).then((inv) => {
      setInvite(inv);
      if (inv.name) setName(inv.name);
      if (inv.email) setEmail(inv.email);
    }).catch(() => setInvite({ valid: false, status: "invalid" }));
  }, [token]);

  const submitForm = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Passwords do not match.");
    setLoading(true);
    try {
      const data = await acceptInvite(token, { name, email, password, confirm_password: confirm });
      setSetupData(data);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const confirmTotp = async (code) => {
    setError("");
    setLoading(true);
    try {
      const res = await totpConfirm({ setup_token: setupData.setup_token, code });
      login(res.token, res.user);
      navigate("/", { replace: true });
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (invite === undefined) {
    return <AuthShell title="Checking invite…"><div className="flex justify-center py-8"><Loader2 className="animate-spin text-slate-400" /></div></AuthShell>;
  }

  if (!invite.valid) {
    return (
      <AuthShell title="Invite unavailable" subtitle="This invitation link can no longer be used.">
        <div className="flex flex-col items-center text-center gap-3 py-6" data-testid="invite-invalid">
          <XCircle size={44} className="text-red-400" />
          <p className="text-sm text-slate-600">
            {invite.status === "expired" ? "This invite has expired (links are valid for 14 days)." :
             invite.status === "accepted" ? "This invite has already been used." :
             "This invite link is invalid."}
          </p>
          <Button variant="outline" onClick={() => navigate("/login")} data-testid="invite-goto-login">Go to Sign In</Button>
        </div>
      </AuthShell>
    );
  }

  if (setupData) {
    return (
      <AuthShell title="Secure your account" subtitle="Set up two-factor authentication to finish.">
        <TwoFactorSetup qrPng={setupData.qr_png} otpauthUri={setupData.otpauth_uri} onSubmit={confirmTotp} loading={loading} error={error} />
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Accept your invitation" subtitle="Create your account to join Soverenx Systems.">
      <form onSubmit={submitForm} className="space-y-5">
        <div>
          <Label className="label-caps">Full Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" autoFocus className="mt-1.5" data-testid="invite-name" />
        </div>
        <div>
          <Label className="label-caps">Email</Label>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={!!invite.email} placeholder="you@soverenxsystems.com.au" className={`mt-1.5 ${invite.email ? "bg-slate-50" : ""}`} data-testid="invite-email" />
        </div>
        <div>
          <Label className="label-caps">Password</Label>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" className="mt-1.5" data-testid="invite-password" />
        </div>
        <div>
          <Label className="label-caps">Confirm Password</Label>
          <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter password" className="mt-1.5" data-testid="invite-confirm" />
        </div>
        {error && <p className="text-sm text-red-600" data-testid="invite-error">{error}</p>}
        <Button type="submit" disabled={loading || !name} className="w-full gap-2 text-white" style={{ background: "#002FA7" }} data-testid="invite-submit">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />} Continue to 2FA
        </Button>
      </form>
    </AuthShell>
  );
};

export default InviteAccept;
